/**
 * Pure helpers for onboarding / account lifecycle (no Firestore I/O).
 * Used by post-auth routing and by onboardingLegacy backfill rules.
 */

import type { User } from 'firebase/auth';

/** UTC timestamp when onboarding v1 was released. Users created before this are legacy. */
export const ONBOARDING_V1_RELEASED_AT = '2026-01-29T00:00:00.000Z';

export function isLegacyUser(authUser: User | null | undefined): boolean {
  if (!authUser?.metadata?.creationTime) return false;
  try {
    const created = new Date(authUser.metadata.creationTime).getTime();
    const released = new Date(ONBOARDING_V1_RELEASED_AT).getTime();
    return created < released;
  } catch {
    return false;
  }
}

function hasValidSocialLink(value: unknown): boolean {
  if (!value) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('www.');
  }
  if (typeof value === 'object') {
    const url = (value as { url?: string }).url;
    if (typeof url === 'string') {
      const trimmed = url.trim();
      return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('www.');
    }
  }
  return false;
}

/**
 * Meaningful creator profile data for legacy bypass / lazy role migration.
 * Do NOT treat provider displayName alone as proof of onboarding (e.g. Google OAuth).
 */
export function hasExistingProfile(userDoc: Record<string, unknown> | null | undefined): boolean {
  if (!userDoc) return false;
  if (userDoc.username || userDoc.usernameLower) return true;

  const socials = userDoc.socials ?? null;
  if (Array.isArray(socials) && socials.some((social) => social?.url || social?.handle)) {
    return true;
  }

  const socialLinks = userDoc.socialLinks ?? null;
  if (Array.isArray(socialLinks) && socialLinks.some(hasValidSocialLink)) return true;
  if (socialLinks && typeof socialLinks === 'object') {
    const values = Object.values(socialLinks);
    if (values.some(hasValidSocialLink)) return true;
  }
  return false;
}

export function shouldBypassOnboarding(
  authUser: User | null | undefined,
  userDoc: Record<string, unknown> | null | undefined,
): boolean {
  if (isLegacyUser(authUser)) return true;
  if (userDoc && hasExistingProfile(userDoc)) return true;
  return false;
}

export function isOnboardingCompleted(userDoc: Record<string, unknown> | null | undefined): boolean {
  if (!userDoc) return false;
  if (userDoc.onboardingCompleted === true) return true;
  if (userDoc.onboardingStatus === 'completed') return true;
  return false;
}

export function shouldTreatAsOnboardingComplete(
  authUser: User | null | undefined,
  userDoc: Record<string, unknown> | null | undefined,
): boolean {
  if (isOnboardingCompleted(userDoc)) return true;
  if (shouldBypassOnboarding(authUser, userDoc)) return true;
  return false;
}

export function needsBackfill(
  authUser: User | null | undefined,
  userDoc: Record<string, unknown> | null | undefined,
): boolean {
  if (!authUser?.uid) return false;
  if (isOnboardingCompleted(userDoc)) return false;
  return shouldBypassOnboarding(authUser, userDoc);
}

/** True when users/{uid} already has an explicit app role (post–role-selection or migrated account). */
export function hasSelectableRoleSet(userDoc: Record<string, unknown> | null | undefined): boolean {
  const r = userDoc?.role ?? userDoc?.userType;
  return r === 'creator' || r === 'influencer' || r === 'brand';
}
